import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, Drawer, InputNumber, Select, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../core/data/redux/store";
import { fetchEmployeeTdsContributions, updateEmployeeTdsContribution, createEmployeeTdsContribution } from "../../../../core/data/redux/payroll/TDS/employeeTdsContribution/employeeTdsContributionAction";
import { fetchFinancialYearsByCompanyId } from "../../../../core/data/redux/financialYear/financialYearActions";
import { fetchEmployeeSalaryStructures } from "../../../../core/data/redux/payroll/payrollStructure/payrollStructure/employeeSalaryStructureActions";
import { filterByStatus } from "../../../../core/utils/filterActiveData";
import { getEmployees } from "../../../../core/data/redux/employee/employeeAction";
import dayjs from "dayjs";
import { fetchAllTaxes } from "../../../../core/data/redux/payroll/TDS/taxes/taxActions";
import { fetchPayscales } from "../../../../core/data/redux/payroll/payrollComponent/payscale/payscaleActions";
import { buildSlabsFromTaxes, calculateMonthlyTDSDynamic } from "../utils/tdsCalculator";
import { useAppConfig } from "../../../../customhooks/useAppConfig";
import { calculateMonthlyGhanaPAYE } from "../utils/ghanaPayeeCalculator";
import { fetchTdsSurchargeCessList } from "../../../../core/data/redux/payroll/TDS/tdsSurchargeCess/tdsSurchargeCessActions";
import { fetchTdsTaxRegimes } from "../../../../core/data/redux/payroll/TDS/tdsTaxRegime/tdsTaxRegimeActions";
import { fetchEarnings } from "../../../../core/data/redux/payroll/payrollComponent/earning/earningActions";
import { fetchTdsDeclarations } from "../../../../core/data/redux/payroll/TDS/tdsDelcaration/tdsDeclarationAction";

const toNum = (v: any) => (v === null || v === undefined || v === '' ? null : Number(v));
const toStr = (v: any) => (v === null || v === undefined ? '' : String(v));


// ---------- helpers (pure) ----------
const parseNum = (v: any) => (v === null || v === undefined ? 0 : Number(v));

const genderFromId = (id?: number | null) => {
  if (id === 1) return "Male";
  if (id === 2) return "Female";
  return "Both"; // fallback if unknown
};

const parseAgeRange = (s?: string | null) => {
  if (!s) return { min: 0, max: Infinity };
  // supports formats like "18 - 60", "60-80", "60 – 80"
  const m = String(s).match(/(\d+)\s*[\-–]\s*(\d+)/);
  if (!m) return { min: 0, max: Infinity };
  const min = Number(m[1]) || 0;
  const max = Number(m[2]) || Infinity;
  return { min, max };
};

const calcAgeYears = (dobISO?: string | null, asOfISO?: string) => {
  if (!dobISO) return 0;
  const base = asOfISO ? dayjs(asOfISO) : dayjs();
  return base.diff(dayjs(dobISO), "year");
};

// Filter taxes by regime + employee age + employee gender
function filterTaxesForEmployee(
  taxes: any[],
  selectedRegimeID: number | null,
  employeeAge: number,
  employeeGender: string
) {
  if (!Array.isArray(taxes) || !selectedRegimeID) return [];

  return (taxes || [])
    .filter((t: any) => String(t.status || "").toLowerCase() === "active")
    .filter((t: any) => {
      const byRegimeId = Number(t.regimeId) === Number(selectedRegimeID);
      const byTaxNameFallback = String(t.tax_name) === String(selectedRegimeID);
      if (!(byRegimeId || byTaxNameFallback)) return false;

      // gender filter (Both matches everyone)
      const taxGender = String(t.gender || "Both");
      const genderOk = taxGender === "Both" || taxGender === employeeGender;

      // age filter
      const { min, max } = parseAgeRange(t.age);
      const ageOk = employeeAge >= min && employeeAge <= max;

      return genderOk && ageOk;
    })
    .map((t: any) => ({
      ...t,
      _min: parseNum(t.min_amount),
      _max: Number(t.max_amount) === 0 ? Infinity : parseNum(t.max_amount),
    }))
    .sort((a: any, b: any) => a._min - b._min);
}

// ---------- component ----------
const EmployeeTdsContributionModal: React.FC<any> = ({
  modalType,
  handleCancel,
  isModalVisible,
  selectedRowData,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { loading } = useSelector((state: any) => state.TDS.employeeTDSContribution);
  const { financialYearsByCompany } = useSelector((state: any) => state.financialYears);
  const companyId = useSelector(
    (state: any) => state.companyDetails.activeCompany?.company_detail_id
  );
  const { salaryStructures } = useSelector((state: any) => state.payroll.salaryStructure);
  const { employees = [] } = useSelector((state: any) => state.employee);
  const activeEmployeeData = filterByStatus(employees, "active", "employee_status");
  const { payscales = [] } = useSelector((state: any) => state.payroll.payrollScall);
  const { taxes = [] } = useSelector((state: any) => state.accounting.taxes);
  const { surchargeCessList = [] } = useSelector((state: any) => state.TDS.tdsSurchargeCess);
  const { regimes = [] } = useSelector((state: any) => state.TDS.tdsTaxRegime);
  const { declarations = [] } = useSelector((state: any) => state.TDS.tdsDeclaration);

  const [employeeData, setEmployeeData] = useState<any>(null);
  const [isPayscaleAutoFilled, setIsPayscaleAutoFilled] = useState(false);
  const [lockAutoSlab, setLockAutoSlab] = useState(false);

  const config = useAppConfig();
  const [formData, setFormData] = useState({
    EmployeeID: "",
    PayScaleID: "",
    FinancialYearID: null as number | null,
    MonthYear: "",
    TaxID: null as number | null,
    Selected_Earning_IDs: [] as number[],
    TaxRegimeID: null as number | null,
    SurchargeCessID: null as number | null,
    ApplicableIncome: 0,
    CalculatedTax: 0,
    CreatedBy: user?.UserId || 1,
    ModifiedBy: user?.UserId || 1,
    ActualIncome: 0,
  });
  const canCreate = Number(formData.CalculatedTax) > 0;

  const [errors, setErrors] = useState<any>({});

  const resetForm = useCallback(() => {
    setFormData({
      EmployeeID: "",
      PayScaleID: "",
      FinancialYearID: null,
      MonthYear: "",
      TaxID: null,
      Selected_Earning_IDs: [],
      TaxRegimeID: null,
      SurchargeCessID: null,
      ApplicableIncome: 0,
      CalculatedTax: 0,
      CreatedBy: user?.UserId || 1,
      ModifiedBy: user?.UserId || 1,
      ActualIncome: 0,
    });
    setErrors({});
  }, [user]);

  // ---------- initial fetches ----------
  useEffect(() => {
    if (companyId) dispatch(fetchFinancialYearsByCompanyId(companyId));
  }, [dispatch, companyId]);

  useEffect(() => {
    dispatch(getEmployees());
    dispatch(fetchAllTaxes());
    dispatch(fetchPayscales());
    dispatch(fetchEmployeeSalaryStructures());
    dispatch(fetchTdsSurchargeCessList());
    dispatch(fetchEarnings());
    dispatch(fetchTdsDeclarations());
  }, [dispatch]);

  useEffect(() => {
    if (!regimes.length) {
      dispatch(fetchTdsTaxRegimes());
    }
  }, [dispatch, regimes.length]);

  // Reset slab choice when regime changes
  useEffect(() => {
    setFormData((prev) => (prev.TaxID === null ? prev : { ...prev, TaxID: null }));
    setLockAutoSlab(false);
  }, [formData.TaxRegimeID]);

  // Load employee salary structure
  useEffect(() => {
    if (formData.EmployeeID) {
      const salaryData = salaryStructures.find(
        (emp: any) => emp.employee_id === parseInt(formData.EmployeeID as any, 10)
      );
      setEmployeeData(salaryData || null);
    } else {
      setEmployeeData(null);
    }
  }, [formData.EmployeeID, salaryStructures]);

  // Auto-fill payscale based on employee experience
  useEffect(() => {
    if (formData.EmployeeID && payscales?.length > 0) {
      const selectedEmp = activeEmployeeData.find(
        (emp: any) => String(emp.EmployeeID) === String(formData.EmployeeID)
      );
      const empExperience = selectedEmp?.experience;

      if (empExperience !== null && empExperience !== undefined) {
        const matchedPayscale = payscales.find(
          (ps: any) => ps.ExperienceLevel === String(empExperience)
        );
        if (matchedPayscale) {
          setFormData((prev) =>
            prev.PayScaleID === matchedPayscale.Id
              ? prev
              : { ...prev, PayScaleID: matchedPayscale.Id }
          );
          setIsPayscaleAutoFilled(true);
        } else {
          setIsPayscaleAutoFilled(false);
        }
      } else {
        setIsPayscaleAutoFilled(false);
      }
    }
  }, [formData.EmployeeID, formData.PayScaleID, payscales, activeEmployeeData]);


  useEffect(() => {
    if (!isModalVisible) return;

    if (modalType === "edit" && selectedRowData) {
      setFormData((prev: any) => ({
        ...prev,
        EmployeeID: toStr(selectedRowData.EmployeeID),
        PayScaleID: toNum(selectedRowData.PayScaleID),               // antd Select options use numbers
        FinancialYearID: toNum(selectedRowData.FinancialYearID),
        MonthYear: toStr(selectedRowData.MonthYear).slice(0, 7),     // "YYYY-MM"
        TaxID: toNum(selectedRowData.TaxID),
        Selected_Earning_IDs: Array.isArray(selectedRowData.Selected_Earning_IDs)
          ? selectedRowData.Selected_Earning_IDs.map((x: any) => Number(x))
          : [],
        TaxRegimeID: toNum(selectedRowData.TaxRegimeID),
        SurchargeCessID: toNum(selectedRowData.SurchargeCessID),
        ApplicableIncome: Number(selectedRowData.ApplicableIncome) || 0,
        CalculatedTax: Number(selectedRowData.CalculatedTax) || 0,
        CreatedBy: user?.UserId || 1,
        ModifiedBy: user?.UserId || 1,
      }));

      setLockAutoSlab(true);

      setIsPayscaleAutoFilled(!!selectedRowData.PayScaleID);
    } else if (modalType !== "edit") {
      resetForm();
      setLockAutoSlab(false);
    }
  }, [isModalVisible, modalType, selectedRowData, user]);

  // ---------- MEMOIZED DERIVED VALUES ----------
    const annualIncome = useMemo(
      () => formData.ApplicableIncome * 12,
      [formData.ApplicableIncome]
    );

  const { employeeAge, employeeGender } = useMemo(() => {
    const emp = employees.find(
      (e: any) => String(e.EmployeeID) === String(formData.EmployeeID)
    );
    return {
      employeeAge: calcAgeYears(emp?.DateOfBirth),
      employeeGender: genderFromId(emp?.GenderId),
    };
  }, [employees, formData.EmployeeID]);

  const regimeEmployeeTaxes = useMemo(
    () =>
      filterTaxesForEmployee(taxes, formData.TaxRegimeID, employeeAge, employeeGender),
    [taxes, formData.TaxRegimeID, employeeAge, employeeGender]
  );

  const activeSlabs = useMemo(
    () => buildSlabsFromTaxes(regimeEmployeeTaxes),
    [regimeEmployeeTaxes]
  );

  const selectedRegime = useMemo(
    () => regimes?.find((r: any) => r.TaxRegimeID === formData.TaxRegimeID) || null,
    [regimes, formData.TaxRegimeID]
  );

  const selectedCess = useMemo(
    () =>
      surchargeCessList?.find((s: any) => s.SurchargeCessID === formData.SurchargeCessID) ||
      null,
    [surchargeCessList, formData.SurchargeCessID]
  );

  // Seed Selected_Earning_IDs when employeeData loads
  useEffect(() => {
    if (employeeData?.earnings?.length) {
      const earningIds = employeeData.earnings.map((e: any) => e.id);
      setFormData((prev) => {
        const sameLength = prev.Selected_Earning_IDs.length === earningIds.length;
        const sameContent =
          sameLength &&
          prev.Selected_Earning_IDs.every((id, i) => id === earningIds[i]);
        return sameContent ? prev : { ...prev, Selected_Earning_IDs: earningIds };
      });
    }
  }, [employeeData]);

  // ---------- CALCULATION EFFECT (guarded setState) ----------
  useEffect(() => {
    // If no employee data or no earnings selected: only update if needed
    if (!employeeData || formData.Selected_Earning_IDs.length === 0) {
      setFormData((prev) => {
        if (prev.ApplicableIncome === 0 && prev.CalculatedTax === 0) return prev;
        return { ...prev, ApplicableIncome: 0, CalculatedTax: 0 };
      });
      return;
    }

    // Sum selected earnings (monthly)
    const totalIncome = formData.Selected_Earning_IDs
      .map((id: number) => {
        const earning = employeeData.earnings.find((e: any) => e.id === id);
        return earning ? Number(earning.amount) : 0;
      })
      .reduce((acc: number, amount: number) => acc + amount, 0);

    const canUseVIADeductions =
      selectedRegime?.AllowsChapterVI_ADeductions
        ? Number(selectedRegime.AllowsChapterVI_ADeductions) === 1
        : false;

    // Approved declarations for this employee in this FY
    // console.log({declarations})
    const approvedDecl = (declarations || []).filter((d: any) => {
      return (
        String(d.EmployeeID) === String(formData.EmployeeID) &&
        String(d.FinancialYearID) === String(formData.FinancialYearID) &&
        String(d.VerificationStatus || "").toLowerCase() === "approved"
      );
    });

    // console.log(approvedDecl,{approvedDecl})

    const additionalDeductionsAnnual = canUseVIADeductions
      ? approvedDecl.reduce((sum: number, d: any) => sum + parseNum(d.VerifiedAmount), 0)
      : 0;

    // console.log(additionalDeductionsAnnual,"form modal additionalDeductionsAnnual")

    let monthlyTDS = 0;
    let taxableIncome = 0;
    if (config?.country === "Ghana") {
      monthlyTDS = Number((calculateMonthlyGhanaPAYE(totalIncome * 12) || 0).toFixed(2));
    } else {
      const result:any = calculateMonthlyTDSDynamic({
        annualIncome: totalIncome * 12,
        slabs: activeSlabs, // memoized
        regime: selectedRegime,
        surchargeCess: selectedCess,
        additionalDeductionsAnnual,
      });
      monthlyTDS = result.monthlyTDS;
      taxableIncome = result.taxableIncome;
    }

    const nextApplicable =  totalIncome * 12;
    const nextTax = Number((monthlyTDS ?? 0).toFixed(2));

    // ✅ Only update if something actually changed
    setFormData((prev) => {
      if (prev.ApplicableIncome === nextApplicable && prev.CalculatedTax === nextTax && prev.ActualIncome === taxableIncome) {
        return prev;
      }
      return {
        ...prev,
        ApplicableIncome: nextApplicable,
        CalculatedTax: nextTax,
        ActualIncome: taxableIncome,
      };
    });
  }, [
    employeeData,
    formData.Selected_Earning_IDs,
    formData.EmployeeID,
    formData.FinancialYearID,
    selectedRegime,
    selectedCess,
    declarations,
    config?.country,
    activeSlabs,
  ]);

  // ---------- AUTO SLAB SELECTION (guarded) ----------
  useEffect(() => {
    if (!annualIncome || annualIncome <= 0) {
      setFormData((prev) => (prev.TaxID === null ? prev : { ...prev, TaxID: null }));
      setLockAutoSlab(false);
      return;
    }
    if (!formData.TaxRegimeID || lockAutoSlab) return;

    const match = regimeEmployeeTaxes.find(
      (t: any) => annualIncome >= t._min && annualIncome <= t._max
    );
    if (match && match.tax_id !== formData.TaxID) {
      setFormData((prev) => ({ ...prev, TaxID: match.tax_id }));
    }
  }, [annualIncome, formData.TaxRegimeID, lockAutoSlab, regimeEmployeeTaxes, formData.TaxID]);

  // ---------- form helpers ----------
  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors: any = {};
    let valid = true;

    if (!formData.EmployeeID) {
      newErrors.EmployeeID = "Employee is required";
      valid = false;
    }
    if (!formData.PayScaleID) {
      newErrors.PayScaleID = "Payscale is required";
      valid = false;
    }
    if (!formData.FinancialYearID) {
      newErrors.FinancialYearID = "Financial Year is required";
      valid = false;
    }
    if (!formData.MonthYear) {
      newErrors.MonthYear = "Month and Year is required";
      valid = false;
    }
    if (!formData.TaxID) {
      newErrors.TaxID = "Tax is required";
      valid = false;
    }
    if (!formData.TaxRegimeID) {
      newErrors.TaxRegimeID = "Tax Regime is required";
      valid = false;
    }
    if (!formData.SurchargeCessID) {
      newErrors.SurchargeCessID = "Surcharge & Cess is required";
      valid = false;
    }
    if (!formData.Selected_Earning_IDs || formData.Selected_Earning_IDs.length === 0) {
      newErrors.Selected_Earning_IDs = "At least one earning must be selected";
      valid = false;
    }
    if (
      formData.CalculatedTax === null ||
      formData.CalculatedTax === undefined ||
      formData.CalculatedTax < 0
    ) {
      newErrors.CalculatedTax = "Calculated Tax is required and must be 0 or more";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      ...formData,
      CreatedBy: user?.UserId || 1,
      ModifiedBy: user?.UserId || 1,
    };
    try {
      if (modalType === "edit") {
        await dispatch(
          updateEmployeeTdsContribution({ id: selectedRowData?.EmployeeTaxMapID, payload })
        ).unwrap();
        message.success("Record updated successfully");
      } else {
        await dispatch(createEmployeeTdsContribution(payload)).unwrap();
        message.success("Record created successfully");
      }
      handleCancel();
      resetForm();
      await dispatch(fetchEmployeeTdsContributions());
    } catch (err: any) {
      const errorMessage = err?.error || "Something went wrong";
      message.error(errorMessage);
    }
  };

  // ---------- UI ----------
  return (
    <Drawer
      title={modalType === "edit" ? "Edit Employee TDS Contribution" : "Add Employee TDS Contribution"}
      placement="right"
      width={700}
      onClose={() => {
        handleCancel();
        resetForm();
      }}
      visible={isModalVisible}
    >
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">
            Financial Year <span style={{ color: "red" }}>*</span>
          </label>
          <Select
            value={formData.FinancialYearID}
            onChange={(val) => handleChange("FinancialYearID", val)}
            className="w-100"
            placeholder="Select financial year"
            allowClear
            options={(financialYearsByCompany || []).map((fy: any) => ({
              value: fy.id,
              label: fy.name,
            }))}
          />
          {errors.FinancialYearID && <div className="text-danger">{errors.FinancialYearID}</div>}
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">
            Month and Year <span style={{ color: "red" }}>*</span>
          </label>
          <DatePicker.MonthPicker
            value={formData.MonthYear ? dayjs(formData.MonthYear, "YYYY-MM") : null}
            onChange={(date: dayjs.Dayjs | null) => {
              if (date) {
                handleChange("MonthYear", date.format("YYYY-MM"));
              } else {
                handleChange("MonthYear", "");
              }
            }}
            className="w-100"
            placeholder="Select month and year"
            format="YYYY-MM"
            disabled={modalType === "edit"}
          />
          {errors.MonthYear && <div className="text-danger">{errors.MonthYear}</div>}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">
            Employee Name <span className="text-danger"> *</span>
          </label>
          <select
            className="form-control"
            value={formData.EmployeeID}
            onChange={(e) => handleChange("EmployeeID", e.target.value)}
          >
            <option value="">Select Employee Name</option>
            {[...activeEmployeeData]
              .sort((a, b) => a.Name.localeCompare(b.Name))
              .map((emp: any) => (
                <option key={emp.EmployeeID} value={emp.EmployeeID}>
                  {emp.Name}
                </option>
              ))}
          </select>
          {errors.EmployeeID && <div className="text-danger">{errors.EmployeeID}</div>}
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">
            Payscale&nbsp; <span className="text-danger"> *</span>
          </label>
          <select
            className="form-select"
            value={formData.PayScaleID}
            onChange={(e) => handleChange("PayScaleID", e.target.value)}
            disabled={isPayscaleAutoFilled}
          >
            <option value="">Select Payscale</option>
            {payscales?.map((pay: any) => (
              <option key={pay.Id} value={pay.Id}>
                {pay.SalaryRangeMin} - {pay.SalaryRangeMax}
              </option>
            ))}
          </select>
          {errors.PayScaleID && <div className="text-danger">{errors.PayScaleID}</div>}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">
            Tax Regime <span className="text-danger">*</span>
          </label>
          <Select
            value={formData.TaxRegimeID}
            onChange={(val) => handleChange("TaxRegimeID", val)}
            className="w-100"
            placeholder="Select Tax Regime"
            allowClear
          >
            {regimes?.map((reg: any) => (
              <Select.Option key={reg.TaxRegimeID} value={reg.TaxRegimeID}>
                {reg.RegimeName} ({reg.FinancialYearName})
              </Select.Option>
            ))}
          </Select>
          {errors.TaxRegimeID && <div className="text-danger">{errors.TaxRegimeID}</div>}
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">
            Surcharge & Cess <span className="text-danger">*</span>
          </label>
          <Select
            value={formData.SurchargeCessID}
            onChange={(val) => handleChange("SurchargeCessID", val)}
            className="w-100"
            placeholder="Select Surcharge & Cess"
            allowClear
          >
            {surchargeCessList?.map((sc: any) => (
              <Select.Option key={sc.SurchargeCessID} value={sc.SurchargeCessID}>
                Cess {sc.HealthAndEducationCessRate_Percentage}%
                {Number(sc.SurchargeRate1_Percentage) > 0 &&
                  ` | Surcharge1: ${sc.SurchargeRate1_Percentage}%`}
                {Number(sc.SurchargeRate2_Percentage) > 0 &&
                  ` | Surcharge2: ${sc.SurchargeRate2_Percentage}%`}
              </Select.Option>
            ))}
          </Select>
          {errors.SurchargeCessID && <div className="text-danger">{errors.SurchargeCessID}</div>}
        </div>
      </div>

      <div className="row">
        {employeeData && (
          <div className="col-md-12 mb-3">
            <label className="form-label">Select Earnings</label>
            <Select
              mode="multiple"
              allowClear
              className="w-100 custom-select"
              placeholder="Select earnings"
              value={formData.Selected_Earning_IDs}
              onChange={(value) => handleChange("Selected_Earning_IDs", value)}
            >
              {employeeData.earnings.map((earning: any) => (
                <Select.Option key={earning.id} value={earning.id}>
                  {earning.earning_name} - ₹{earning.amount}
                </Select.Option>
              ))}
            </Select>
            {errors.Selected_Earning_IDs && (
              <div className="text-danger mt-1">{errors.Selected_Earning_IDs}</div>
            )}
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-md-12 mb-3">
          <label className="form-label">
            Select TDS Slab <span style={{ color: "red" }}>*</span>
          </label>
          <Select
            value={formData.TaxID}
            onChange={(value) => {
              setLockAutoSlab(true);
              handleChange("TaxID", value);
            }}
            className="w-100"
            placeholder={formData.TaxRegimeID ? "Select TDS" : "Select Tax Regime first"}
            allowClear
            disabled={!formData.TaxRegimeID}
          >
            {regimeEmployeeTaxes
              .filter((tax: any) => {
                if (!annualIncome) return true; 
                return annualIncome >= tax._min && annualIncome <= tax._max;
              })
              .map((tax: any) => (
                <Select.Option key={tax.tax_id} value={tax.tax_id}>
                  {tax.regimeName} • {tax.tax_percentage}% &nbsp;(
                  {tax.min_amount} - {Number(tax.max_amount) === 0 ? "∞" : tax.max_amount})
                  {tax.age ? ` • Age: ${tax.age}` : ""}
                  {tax.gender && tax.gender !== "Both" ? ` • ${tax.gender}` : ""}
                </Select.Option>
              ))}
          </Select>
          {errors.TaxID && <div className="text-danger">{errors.TaxID}</div>}
        </div>
      </div>
       

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">Projected Annual Applicable Income</label>
          <InputNumber min={0} value={formData.ApplicableIncome} disabled style={{ width: "100%" }} />
        </div>
       
        <div className="col-md-6 mb-3">
          <label className="form-label">Actual Annual Taxable Income</label>
          <InputNumber
            min={0}
            value={formData.ActualIncome}
            disabled
            style={{ width: "100%" }}
            formatter={(value) => {
              if (value === undefined || value === null) return "0.00";
              const num = typeof value === "string" ? parseFloat(value) : value;
              return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
            }}
          />
        </div>
      </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Monthly Calculated Tax</label>
          <InputNumber
            min={0}
            value={formData.CalculatedTax}
            disabled
            style={{ width: "100%" }}
            formatter={(value) => {
              if (value === undefined || value === null) return "0.00";
              const num = typeof value === "string" ? parseFloat(value) : value;
              return isNaN(num) ? "0.00" : num.toFixed(2);
            }}
          />
          {errors.CalculatedTax && <div className="text-danger">{errors.CalculatedTax}</div>}
        </div>

      <div className="modal-footer mt-4">
        <button className="btn btn-secondary me-2" onClick={handleCancel}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading || (modalType !== "edit" && !canCreate)}
        >
          {loading && <span className="spinner-border spinner-border-sm me-2" />}
          {modalType === "edit" ? "Update" : "Create"}
        </button>
      </div>
    </Drawer>
  );
};

export default EmployeeTdsContributionModal;
